/**
 * Report Settings Service
 *
 * Manages per-workspace configuration of which reports are visible and required
 * on the Reports page. Also holds the canonical registry of all platform reports.
 */

import { getSupabaseClient } from './supabaseClient';
import type { AppArea } from './appAreas';
import { isFeatureEnabled, type FeatureFlagValue } from './featureFlags';

const TABLE = 'workspace_report_settings';

// ── Registry ─────────────────────────────────────────────────────────────────

/**
 * Domain groups the Reports home is organized under (display order below).
 * This replaces the old ad-hoc Required/Default split as the primary axis.
 */
export type ReportDomain = 'items' | 'sessions' | 'cycles' | 'strategy' | 'relationships' | 'monitoring' | 'decisions' | 'custom';

/**
 * Canonical authority tier (docs/1-canon/08-reporting-read-model-specification.md §3.5).
 * `none` = a working/analysis surface that does not carry a canon authority level.
 */
export type ReportAuthority = 'informational' | 'advisory' | 'authoritative' | 'none';

/**
 * Where a report appears:
 *  - `home`      — has a real workspace-level landing page; gets a card on the Reports home.
 *  - `drilldown` — a per-entity artifact (keyed by id) reached by drilling in from a browse
 *                  page or governance surface. Kept in the registry for authority labeling and
 *                  admin visibility config, but does NOT get a standalone home card (which would
 *                  be a dead link, since these routes require an :id).
 */
export type ReportSurface = 'home' | 'drilldown';

/**
 * How a report's data is produced.
 *  - `aggregate`          — a bespoke page that summarizes live stores.
 *  - `canonical-artifact` — a persisted per-id report artifact (read-model).
 *  - `item-query`         — a preset over the item-list/custom-report engine
 *                           (see lib/reportPresets.ts); inherits the item-field pipeline.
 */
export type ReportKind = 'aggregate' | 'canonical-artifact' | 'item-query';

export interface ReportDefinition {
  key: string;
  name: string;
  description: string;
  /** Legacy grouping used by the Report Settings admin page. */
  section: 'analysis' | 'canonical' | 'overview';
  href: string;
  /** Domain group on the Reports home. */
  domain: ReportDomain;
  /** Canon authority tier — drives the badge on the home. */
  authority: ReportAuthority;
  /** Home card vs drill-down-only artifact. */
  surface: ReportSurface;
  /** How the report is produced. */
  kind: ReportKind;
  /** Capability required to view. Reserved for role-aware gating (PRD Phase 3); not yet enforced. */
  requiredCapability?: string;
  /** Workspace module the report lives behind; the home hides it when the module is off. */
  moduleKey?: string;
  /** A feature switch the report's page also needs (as the sidebar checks it). */
  featureFlag?: string;
  /**
   * The areas whose Reports menu shows this report, as the platform places it.
   * A workspace can change it (Settings → Reports); see effectiveReportAreas.
   * Drill-down reports carry none: they are reached from the page they report on.
   */
  areas: AppArea[];
}

/**
 * The single source of truth for platform reports.
 *
 * `ReportsIndex`, the Report Settings admin page, and (progressively) the sidebars/command
 * palette all render from this list. One entry → one route → one label, so the catalog can no
 * longer drift across surfaces.
 */
export const REPORT_REGISTRY: ReportDefinition[] = [
  // ── Items & Readiness ──────────────────────────────────────────────────────
  {
    // Was 'item-readiness' (Item Readiness). The report grew into the Pipeline
    // Health dashboard (roadmap §2.1) and kept this slot; a workspace that had
    // hidden the old key sees the new report by default.
    key: 'pipeline-health',
    name: 'Pipeline Health',
    description: 'What is the state of the backlog right now: where every item stands, its status, category, readiness gaps, and age.',
    section: 'analysis',
    href: '/prioritization/reports/pipeline-health',
    domain: 'items',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    areas: ['priorities'],
  },
  // ── Sessions & Methods ─────────────────────────────────────────────────────
  {
    key: 'session-reports',
    name: 'Session Reports',
    description: 'All prioritization sessions, their configurations, tools used, and ranking results. Drill into any session for its Session Result.',
    section: 'overview',
    href: '/prioritization/reports/sessions',
    domain: 'sessions',
    authority: 'informational',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    areas: ['priorities'],
  },
  {
    key: 'session-results',
    name: 'Session Results',
    description: 'Atomic, immutable record of what happened in a single session. Reached by drilling in from Session Reports.',
    section: 'canonical',
    href: '/prioritization/reports/sessions',
    domain: 'sessions',
    authority: 'informational',
    surface: 'drilldown',
    kind: 'canonical-artifact',
    areas: [],
  },
  // ── Cycles & Governance ────────────────────────────────────────────────────
  {
    key: 'cycle-execution',
    name: 'Cycle Execution',
    description: 'Cycle and session progress, status breakdowns, and execution metrics.',
    section: 'analysis',
    href: '/prioritization/reports/execution',
    domain: 'cycles',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    areas: ['priorities'],
  },
  {
    // The decision latency report (L2) and its decision-to-execution trend (L3).
    key: 'decision-latency',
    name: 'Decision Latency',
    description: 'Where the time goes between an ask and an authorized plan, stage by stage — and how much of what was decided has started.',
    section: 'analysis',
    href: '/prioritization/decision-latency',
    domain: 'cycles',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    moduleKey: 'decision_latency',
    areas: ['plans', 'action'],
  },
  {
    key: 'cycle-reports',
    name: 'Cycle Reports',
    description: 'Detailed reports for each cycle including prerequisite progress, session outcomes, and prioritization results.',
    section: 'overview',
    href: '/prioritization/reports/cycles',
    domain: 'cycles',
    authority: 'authoritative',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    areas: ['priorities', 'plans'],
  },
  {
    key: 'loop-rollup',
    name: 'Loop Roll-Up',
    description: 'Aggregate view of all sessions within a Catchball loop. Reached by drilling in from a cycle or priority list.',
    section: 'canonical',
    href: '/prioritization/reports/cycles',
    domain: 'cycles',
    authority: 'advisory',
    surface: 'drilldown',
    kind: 'canonical-artifact',
    areas: [],
  },
  {
    key: 'priority-sets',
    name: 'Priority Lists',
    description: 'Authoritative cycle-level product priorities with governance trail. Reached by drilling in from a cycle.',
    section: 'canonical',
    href: '/prioritization/reports/cycles',
    domain: 'cycles',
    authority: 'authoritative',
    surface: 'drilldown',
    kind: 'canonical-artifact',
    areas: [],
  },
  // ── Relationships ──────────────────────────────────────────────────────────
  {
    key: 'relationship-health',
    name: 'Relationship Health',
    description: 'Coverage and health of item relationships — dependencies, conflicts, and lineage gaps across the workspace.',
    section: 'analysis',
    href: '/prioritization/reports/relationship-health',
    domain: 'relationships',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    requiredCapability: 'analytics.view',
    areas: ['priorities', 'plans'],
  },
  // ── Item-query presets ──────────────────────────────────────────────────────
  // These reuse the custom-report engine via a preset (lib/reportPresets.ts), so
  // they inherit the item-field pipeline: any field added to the item list is
  // available to them for free. They open the designer pre-configured.
  {
    key: 'intake-clarity',
    name: 'Intake & Clarity Funnel',
    description: 'Active items that are not yet base-complete, grouped by category — the backlog still in intake.',
    section: 'analysis',
    href: '/prioritization/reports/custom/new?preset=intake-clarity',
    domain: 'items',
    authority: 'none',
    surface: 'home',
    kind: 'item-query',
    requiredCapability: 'analytics.view',
    areas: ['priorities'],
  },
  {
    key: 'strategy-alignment',
    name: 'Strategy Alignment',
    description: 'How active items distribute across strategic buckets — coverage and gaps against strategy.',
    section: 'analysis',
    href: '/prioritization/reports/custom/new?preset=strategy-alignment',
    domain: 'strategy',
    authority: 'none',
    surface: 'home',
    kind: 'item-query',
    requiredCapability: 'analytics.view',
    areas: ['strategy', 'priorities'],
  },
  // ── Monitoring the plan in force ────────────────────────────────────────────
  // Were the Action menu's "Monitoring" links; now Action's Reports menu lists them.
  {
    key: 'drift',
    name: 'Drift Detection',
    description: 'When active work diverges from the published priority lists.',
    section: 'analysis',
    href: '/prioritization/monitor/drift',
    domain: 'monitoring',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    areas: ['action'],
  },
  {
    key: 'monitor-analytics',
    name: 'Analytics & Insights',
    description: 'Prioritization patterns, list health, velocity and drift trends.',
    section: 'analysis',
    href: '/prioritization/monitor/analytics',
    domain: 'monitoring',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    areas: ['action'],
  },
  {
    key: 'evidence-quality',
    name: 'Evidence Quality',
    description: 'Evidence coverage and quality across all items.',
    section: 'analysis',
    href: '/prioritization/monitor/evidence',
    domain: 'monitoring',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    moduleKey: 'evidence',
    areas: ['action'],
  },
  // ── Decision quality ────────────────────────────────────────────────────────
  // These already sit in the Decisions menu, which every area shows, so the
  // platform places them in no Reports menu (no link drawn twice). A workspace
  // can place them in Settings → Reports.
  {
    key: 'bias-audit',
    name: 'Cognitive Bias Audit',
    description: 'AI-assisted analysis of decision patterns that may reflect known cognitive biases.',
    section: 'analysis',
    href: '/prioritization/insights/cognitive-bias',
    domain: 'decisions',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    moduleKey: 'bias_audit',
    areas: [],
  },
  {
    key: 'judgment-index',
    name: 'Judgment Index',
    description: 'Judgment profiles for your people, recorded by a certified practitioner, read alongside the prioritization work.',
    section: 'analysis',
    href: '/prioritization/insights/judgment-index',
    domain: 'decisions',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    moduleKey: 'judgment_index',
    featureFlag: 'judgment_index',
    areas: [],
  },
  {
    key: 'judgment-analytics',
    name: 'Judgment Analytics',
    description: 'Whose judgment to seek, for which kind of call — from track record, not reputation.',
    section: 'analysis',
    href: '/prioritization/insights/judgment-analytics',
    domain: 'decisions',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    moduleKey: 'judgment_analytics',
    featureFlag: 'judgment_analytics',
    areas: [],
  },
  {
    key: 'calibration',
    name: 'Calibration',
    description: 'How accurate prioritization scores were: predicted against actual outcomes.',
    section: 'analysis',
    href: '/prioritization/calibration',
    domain: 'decisions',
    authority: 'none',
    surface: 'home',
    kind: 'aggregate',
    moduleKey: 'decision_calibration',
    areas: [],
  },
];

// ── Home helpers ───────────────────────────────────────────────────────────────

/** Display order + labels for domain groups on the Reports home. */
export const REPORT_DOMAIN_ORDER: { domain: ReportDomain; label: string; description: string }[] = [
  { domain: 'items', label: 'Items & Readiness', description: 'Where items stand and how ready they are to prioritize.' },
  { domain: 'sessions', label: 'Sessions & Methods', description: 'What happened in prioritization sessions and which tools were used.' },
  { domain: 'cycles', label: 'Cycles & Governance', description: 'Cycle execution, roll-ups, and authoritative priorities.' },
  { domain: 'strategy', label: 'Strategy & Outcomes', description: 'How priorities align to strategy and what was decided.' },
  { domain: 'relationships', label: 'Relationships', description: 'Dependencies, conflicts, and lineage across items.' },
  { domain: 'monitoring', label: 'Monitoring', description: 'How the plan in force is holding up: drift, trends and evidence.' },
  { domain: 'decisions', label: 'Decision Quality', description: 'How well decisions were made, and whose judgment to seek.' },
];

/** Reports that have a real landing page and should appear as cards on the home. */
export const HOME_REPORTS = REPORT_REGISTRY.filter((r) => r.surface === 'home');

// ── DB types ──────────────────────────────────────────────────────────────────

export interface WorkspaceReportSetting {
  id: string;
  workspaceId: string;
  reportKey: string;
  isVisible: boolean;
  isRequired: boolean;
  /** This workspace's placement; null = the platform's (ReportDefinition.areas). */
  areas: AppArea[] | null;
  createdAt: string;
  updatedAt: string;
}

function rowToSetting(row: Record<string, unknown>): WorkspaceReportSetting {
  return {
    id: row.id as string,
    workspaceId: row.workspace_id as string,
    reportKey: row.report_key as string,
    isVisible: row.is_visible as boolean,
    isRequired: row.is_required as boolean,
    areas: (row.areas as AppArea[] | null) ?? null,
    createdAt: row.created_at as string,
    updatedAt: row.updated_at as string,
  };
}

// ── Service functions ─────────────────────────────────────────────────────────

/** Fetch all report settings for a workspace, seeding defaults if none exist. */
export async function listReportSettings(
  workspaceId: string
): Promise<{ data: WorkspaceReportSetting[]; error: string | null }> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from(TABLE)
    .select('id, workspace_id, report_key, is_visible, is_required, areas, created_at, updated_at')
    .eq('workspace_id', workspaceId)
    .order('report_key');

  if (error) return { data: [], error: error.message };

  if (!data || data.length === 0) {
    const { error: seedError } = await supabase.rpc('seed_workspace_report_settings', {
      p_workspace_id: workspaceId,
    });
    if (seedError) return { data: [], error: seedError.message };

    const { data: seeded, error: refetchError } = await supabase
      .from(TABLE)
      .select('id, workspace_id, report_key, is_visible, is_required, areas, created_at, updated_at')
      .eq('workspace_id', workspaceId)
      .order('report_key');

    if (refetchError) return { data: [], error: refetchError.message };
    return { data: (seeded as Record<string, unknown>[]).map(rowToSetting), error: null };
  }

  return { data: (data as Record<string, unknown>[]).map(rowToSetting), error: null };
}

/** Set is_visible for a report. */
export async function setReportVisible(
  workspaceId: string,
  reportKey: string,
  isVisible: boolean
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      { workspace_id: workspaceId, report_key: reportKey, is_visible: isVisible, updated_at: new Date().toISOString() },
      { onConflict: 'workspace_id,report_key' }
    );
  return { error: error?.message ?? null };
}

/** Set is_required for a report. Required reports are always visible. */
export async function setReportRequired(
  workspaceId: string,
  reportKey: string,
  isRequired: boolean
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const updates: Record<string, unknown> = {
    workspace_id: workspaceId,
    report_key: reportKey,
    is_required: isRequired,
    updated_at: new Date().toISOString(),
  };
  // Required implies visible
  if (isRequired) updates.is_visible = true;

  const { error } = await supabase
    .from(TABLE)
    .upsert(updates, { onConflict: 'workspace_id,report_key' });
  return { error: error?.message ?? null };
}

// ── Placement by area ─────────────────────────────────────────────────────────

export const REPORT_AREAS: AppArea[] = ['strategy', 'priorities', 'plans', 'action'];

/** Where a report appears in this workspace: its own choice, else the platform's. */
export function effectiveReportAreas(def: ReportDefinition, setting: { areas: AppArea[] | null } | undefined): AppArea[] {
  return setting?.areas ?? def.areas;
}

/**
 * The platform reports an area's Reports menu lists, in registry order: home
 * reports placed in that area, visible in this workspace, whose module is on.
 */
export function reportsForArea(
  area: AppArea,
  settings: Array<Pick<WorkspaceReportSetting, 'reportKey' | 'isVisible' | 'isRequired' | 'areas'>>,
  isModuleOn: (moduleKey: string) => boolean,
): ReportDefinition[] {
  const byKey = new Map(settings.map((s) => [s.reportKey, s]));
  return REPORT_REGISTRY.filter((def) => {
    if (def.surface !== 'home') return false;
    if (def.moduleKey && !isModuleOn(def.moduleKey)) return false;
    if (def.featureFlag && !isFeatureEnabled(def.featureFlag as FeatureFlagValue)) return false;
    const s = byKey.get(def.key);
    if (s && !s.isVisible && !s.isRequired) return false;
    return effectiveReportAreas(def, s).includes(area);
  });
}

/** Set where a report appears in this workspace; null returns it to the platform's placement. */
export async function setReportAreas(
  workspaceId: string,
  reportKey: string,
  areas: AppArea[] | null,
): Promise<{ error: string | null }> {
  const supabase = getSupabaseClient();
  const { error } = await supabase
    .from(TABLE)
    .upsert(
      { workspace_id: workspaceId, report_key: reportKey, areas, updated_at: new Date().toISOString() },
      { onConflict: 'workspace_id,report_key' },
    );
  return { error: error?.message ?? null };
}
